import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { InternationalRequest, InternationalRequestDetail } from '../data/international-request';
import { Subscription } from '../data/subscription';

@Injectable({
  providedIn: 'root',
})
export class InternationalRequestService {
  private irUrl = `${environment.apiUrl}/api/InternationalRequests`;
  private subUrl = `${environment.apiUrl}/api/SubscriptionItems`;
  private emailUrl = `${environment.legacyUrl}/request_email.asp`;

  constructor(private http: HttpClient) { }

  getInternationalRequests(): Observable<InternationalRequest[]> {
    const url = `${this.irUrl}/`;
    return this.http.get<InternationalRequest[]>(url)
      .pipe(
        tap(_ => this.log('fetched international requests')),
        catchError(this.handleError<InternationalRequest[]>('getInternationalRequests'))
      );
  }

  getInternationalRequestsByStatus(status: number, username?: string): Observable<InternationalRequestDetail[]> {
    username = username || "";
    const url = `${this.irUrl}/status?status=${status}&username=${username}`;
    return this.http.get<InternationalRequestDetail[]>(url)
      .pipe(
        tap(_ => this.log('fetched international requests by status')),
        catchError(this.handleError<InternationalRequestDetail[]>('getInternationalRequestsByStatus'))
      );
  }

  getInternationalRequestsByStatusRep(status: number, username: string): Observable<InternationalRequestDetail[]> {
    const url = `${this.irUrl}/status/${username}?status=${status}`;
    return this.http.get<InternationalRequestDetail[]>(url)
      .pipe(
        tap(_ => this.log('fetched international requests by status')),
        catchError(this.handleError<InternationalRequestDetail[]>('getInternationalRequestsByStatusRep'))
      );
  }

  createInternationalRequest(newReq: InternationalRequest): Observable<number> {
    const url = `${this.irUrl}/`;
    return this.http.post<number>(url, newReq)
      .pipe(
        tap(_ => this.log('Created new international request')),
        catchError(this.handleError<number>('createInternationalRequest'))
      );
  }

  denyInternationalRequest(id: number, username: string): Observable<InternationalRequest> {
    const url = `${this.irUrl}/${id}?username=${username}`;
    return this.http.delete<InternationalRequest>(url)
      .pipe(
        tap(_ => this.log('Denied international request')),
        catchError(this.handleError<InternationalRequest>('denyInternationalRequest'))
      );
  }

  approveInternationalRequest(sub: Subscription, id: number, username: string): Observable<InternationalRequest> {
    const url = `${this.subUrl}/approve/${id}?rep=${username}`;
    return this.http.post<InternationalRequest>(url, sub)
      .pipe(
        tap(_ => this.log('Approved international request')),
        catchError(this.handleError<InternationalRequest>('approveInternationalRequest'))
      );
  }

  sendInternationalRequestEmail(pa_code: string, notes: string): Observable<string> {
    const url = `${this.emailUrl}?id=${pa_code}`;
    return this.http.post<string>(url, `{"notes": ${notes}}`)
      .pipe(
        tap(_ => this.log('Sent international request email')),
        catchError(this.handleError<string>('sendInternationalRequestEmail'))
      );
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  private log(message: string) {
    console.log(message);
  }
}